// ============================================================
//  src/pages/settings/WebhookSettingsPage.js
//
//  Shows the user their personal webhook URL and verify token
//  to paste into their Meta App → WhatsApp → Configuration.
//  One token per account — works for all bots on the account.
//  Styles: ./WebhookSettingsPage.css (scoped under .whk-*)
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getWebhookSettings, regenerateWebhookToken } from '../../api/client';
import './WebhookSettingsPage.css';

// ── tiny helpers ─────────────────────────────────────────────
function copyToClipboard(text, label = 'Copied to clipboard') {
  if (!text) return Promise.resolve(false);
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).then(
      () => { toast.success(label); return true; },
      () => { toast.error('Copy failed — please copy manually.'); return false; },
    );
  }
  // Fallback for older browsers
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity  = '0';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  toast.success(label);
  return Promise.resolve(true);
}

function CopyField({ label, hint, icon, value, secret = false, flash }) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(!secret);

  async function copy() {
    const ok = await copyToClipboard(value, `${label} copied`);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1800); }
  }

  const shown = !value ? '—' : revealed ? value : '•'.repeat(Math.min(32, value.length));

  return (
    <div className={`whk-field ${flash ? 'is-flash' : ''}`}>
      <div className="whk-field-top">
        <span className="whk-field-label"><i className={`fa-solid ${icon}`} /> {label}</span>
        {hint && <span className="whk-field-hint">{hint}</span>}
      </div>
      <div className="whk-field-box">
        <code className={`whk-field-value ${!revealed ? 'is-masked' : ''}`}>{shown}</code>
        <div className="whk-field-actions">
          {secret && value && (
            <button type="button" className="whk-icon-btn" onClick={() => setRevealed(r => !r)}
              aria-label={revealed ? 'Hide token' : 'Show token'} title={revealed ? 'Hide' : 'Show'}>
              <i className={`fa-solid ${revealed ? 'fa-eye-slash' : 'fa-eye'}`} />
            </button>
          )}
          <button type="button" className={`whk-copy ${copied ? 'is-copied' : ''}`} onClick={copy} disabled={!value}>
            <i className={`fa-${copied ? 'solid fa-check' : 'regular fa-copy'}`} />
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const STEPS = [
  {
    title: 'Open your Meta app',
    body: <>Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">Meta for Developers</a> → your app → <b>WhatsApp</b> → <b>Configuration</b>.</>,
  },
  {
    title: 'Paste the Callback URL',
    body: <>Under <b>Webhook</b>, click <b>Edit</b> and paste the <b>Callback URL</b> from this page.</>,
  },
  {
    title: 'Paste the Verify Token',
    body: <>Paste the <b>Verify Token</b> into the Verify token field, then click <b>Verify and save</b>.</>,
  },
  {
    title: 'Subscribe to webhook fields',
    body: <>Under <b>Webhook fields</b>, click <b>Manage</b> and subscribe to the fields below. That’s it — every bot on your account is connected.</>,
  },
];

const FIELDS = [
  { name: 'messages',           req: true,  note: 'Incoming messages' },
  { name: 'message_deliveries', req: false, note: 'Delivery receipts' },
  { name: 'message_reads',      req: false, note: 'Read receipts' },
];

export default function WebhookSettingsPage() {
  const [settings, setSettings]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [flashToken, setFlashToken]     = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getWebhookSettings();
      setSettings(res.data ?? null);
    } catch {
      toast.error('Could not load webhook settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRegenerate() {
    setRegenerating(true);
    try {
      const res = await regenerateWebhookToken();
      const newToken = res.data?.verify_token;
      if (newToken) {
        setSettings(prev => ({ ...prev, verify_token: newToken }));
        toast.success('Token regenerated! Update your Meta App webhook settings.');
        setFlashToken(true);
        setTimeout(() => setFlashToken(false), 2200);
      }
      setShowConfirm(false);
    } catch {
      toast.error('Failed to regenerate token.');
    } finally {
      setRegenerating(false);
    }
  }

  function copyBoth() {
    if (!settings?.webhook_url || !settings?.verify_token) return;
    copyToClipboard(`Callback URL: ${settings.webhook_url}\nVerify Token: ${settings.verify_token}`, 'Both values copied');
  }

  if (loading) {
    return (
      <div className="whk">
        <div className="whk-head"><div><span className="whk-sk w40 h28" /><span className="whk-sk w70" /></div></div>
        <div className="whk-grid">
          <div className="whk-card"><span className="whk-sk w30" /><span className="whk-sk h48" /><span className="whk-sk w30" /><span className="whk-sk h48" /></div>
          <div className="whk-card"><span className="whk-sk w50" /><span className="whk-sk w90" /><span className="whk-sk w80" /><span className="whk-sk w70" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="whk">
      {/* Header */}
      <header className="whk-head">
        <div>
          <span className="whk-eyebrow"><i className="fa-solid fa-link" /> Settings</span>
          <h1>Webhook</h1>
          <p>Connect Meta to CommandKit once. These two values work for <b>every WhatsApp bot</b> on your account.</p>
        </div>
        <a className="whk-btn ghost" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">
          <i className="fa-brands fa-meta" /> Open Meta for Developers <i className="fa-solid fa-arrow-up-right-from-square whk-ext" />
        </a>
      </header>

      <div className="whk-grid">
        {/* ── Left: credentials ── */}
        <div className="whk-col">
          <section className="whk-card">
            <div className="whk-card-head">
              <div>
                <h2>Your webhook details</h2>
                <p>Paste these into Meta → WhatsApp → Configuration.</p>
              </div>
              <button type="button" className="whk-btn ghost sm" onClick={copyBoth} disabled={!settings?.webhook_url || !settings?.verify_token}>
                <i className="fa-regular fa-clone" /> Copy both
              </button>
            </div>

            <CopyField label="Callback URL" icon="fa-globe" hint="Same for all bots" value={settings?.webhook_url} />
            <CopyField label="Verify Token" icon="fa-key" hint="Keep this private" value={settings?.verify_token} secret flash={flashToken} />

            <div className="whk-route">
              <div className="whk-route-node"><span className="whk-route-ic meta"><i className="fa-brands fa-meta" /></span><b>Meta</b><em>sends events</em></div>
              <span className="whk-route-line" aria-hidden="true"><i className="fa-solid fa-chevron-right" /></span>
              <div className="whk-route-node"><span className="whk-route-ic ck"><i className="fa-solid fa-code" /></span><b>CommandKit</b><em>reads Phone Number ID</em></div>
              <span className="whk-route-line" aria-hidden="true"><i className="fa-solid fa-chevron-right" /></span>
              <div className="whk-route-node"><span className="whk-route-ic bot"><i className="fa-solid fa-robot" /></span><b>Right bot</b><em>handles the message</em></div>
            </div>
          </section>

          {/* Danger zone */}
          <section className={`whk-card whk-danger ${showConfirm ? 'is-confirming' : ''}`}>
            <div className="whk-danger-row">
              <span className="whk-danger-ic"><i className="fa-solid fa-rotate" /></span>
              <div className="whk-danger-copy">
                <h3>Regenerate verify token</h3>
                <p>Only do this if your token has leaked. Meta stops delivering messages until you paste the new token.</p>
              </div>
              {!showConfirm && (
                <button type="button" className="whk-btn danger-ghost" onClick={() => setShowConfirm(true)} disabled={regenerating}>
                  Regenerate
                </button>
              )}
            </div>
            {showConfirm && (
              <div className="whk-confirm" role="alert">
                <p><i className="fa-solid fa-triangle-exclamation" /> All your bots will stop receiving messages until you update the Verify Token in your Meta app. Continue?</p>
                <div className="whk-confirm-actions">
                  <button type="button" className="whk-btn ghost" onClick={() => setShowConfirm(false)} disabled={regenerating}>Cancel</button>
                  <button type="button" className="whk-btn danger" onClick={handleRegenerate} disabled={regenerating}>
                    {regenerating ? <><span className="whk-spin" /> Regenerating…</> : <>Yes, regenerate</>}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* ── Right: setup guide ── */}
        <aside className="whk-card whk-guide">
          <div className="whk-card-head">
            <div>
              <h2>Connect in 4 steps</h2>
              <p>Takes about two minutes.</p>
            </div>
          </div>

          <ol className="whk-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="whk-step-n">{i + 1}</span>
                <div>
                  <b>{s.title}</b>
                  <p>{s.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="whk-fields">
            {FIELDS.map(f => (
              <div key={f.name} className="whk-fieldrow">
                <code>{f.name}</code>
                <span>{f.note}</span>
                <em className={f.req ? 'is-req' : ''}>{f.req ? 'Required' : 'Optional'}</em>
              </div>
            ))}
          </div>

          <div className="whk-tip">
            <i className="fa-solid fa-lightbulb" />
            <span><b>One token, all bots.</b> Meta includes the Phone Number ID in every event, and CommandKit uses it to route each message to the right bot — no per-bot setup needed.</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
